import React, { useEffect, useState } from "react";
import { Plus, Trash2, Palette, AlertTriangle, PaintBucket, RotateCcw, Triangle, Package, FileText, Upload, History, ChevronRight, Scissors, ArrowUp, ArrowDown, Layers } from "lucide-react";
import { AppLayout, PageHeader } from "@/components/AppLayout";
import {
  DataTable, Toolbar, SearchInput, Btn, Field, Input, Textarea, Select,
  Modal, LengthInput,
} from "@/components/Primitives";
import {
  api, fmtNumber, formatApiErrorDetail, can, saveBlob,
  LENGTHS, BUTT_COLOUR_MAP, colourForButt,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CutPlanModal } from "@/components/CutPlanModal";
import { toast } from "sonner";

const newBatch = () => ({ length: "", thick: "", wide: "", quantity: 0, damaged: "", colour: "", comments: "" });
const newDamagedBatch = () => ({
  damaged_length: "", accepted_length: "", thick: "", wide: "",
  quantity: 0, colour: "", comments: "",
});

export default function Processing() {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [pending, setPending] = useState([]);
  const [recvAll, setRecvAll] = useState([]);
  const [damaged, setDamaged] = useState([]);
  const [awaitingPaint, setAwaitingPaint] = useState([]);
  const [awaitingPointing, setAwaitingPointing] = useState([]);
  const [awaitingBundling, setAwaitingBundling] = useState([]);
  const [ordersMap, setOrdersMap] = useState({});
  const [colourMap, setColourMap] = useState(BUTT_COLOUR_MAP);
  const [q, setQ] = useState("");
  const [paintQty, setPaintQty] = useState({});
  const [cutList, setCutList] = useState([]);
  const [cutProg, setCutProg] = useState({});
  const [reduceQty, setReduceQty] = useState({});
  const [dmgOpen, setDmgOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [salvageTarget, setSalvageTarget] = useState(null);
  const [stockCutOpen, setStockCutOpen] = useState(false);
  const [combineSel, setCombineSel] = useState([]);        // cut plan ids picked for a combined sheet
  const [combineItems, setCombineItems] = useState(null);  // ordered line items (priority) or null
  const [cutReportOpen, setCutReportOpen] = useState(false);
  const [cutReport, setCutReport] = useState(null);
  const [crFrom, setCrFrom] = useState(() => new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10));
  const [crTo, setCrTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [crLoading, setCrLoading] = useState(false);
  const canCreate = can(user?.role, "processing:create");

  const load = async () => {
    const [r1, r2, r3, r4, r5, r6, r7, r8, r9] = await Promise.all([
      api.get("/processing"),
      api.get("/receiving"),
      api.get("/processing/colour-map").catch(() => ({ data: [] })),
      api.get("/stock", { params: { status: "damaged" } }).catch(() => ({ data: [] })),
      api.get("/stock", { params: { status: "awaiting_paint" } }).catch(() => ({ data: [] })),
      api.get("/stock", { params: { requires_pointing: true, pointed: false } }).catch(() => ({ data: [] })),
      api.get("/stock", { params: { requires_bundling: true, bundled: false } }).catch(() => ({ data: [] })),
      api.get("/orders").catch(() => ({ data: [] })),
      api.get("/processing/cut-list").catch(() => ({ data: [] })),
    ]);
    setRecords(r1.data);
    setRecvAll(r2.data || []);
    setPending(r2.data.filter((x) => x.status === "pending_processing"));
    setDamaged(r4.data || []);
    setAwaitingPaint((r5.data || []).filter((s) => !s.cut_ref));
    setCutList(r9.data || []);
    setAwaitingPointing((r6.data || []).filter((s) => s.status !== "dispatched"));
    setAwaitingBundling((r7.data || []).filter((s) => s.status !== "dispatched"));
    const om = {};
    (r8.data || []).forEach((o) => { om[o.order_number] = o; });
    setOrdersMap(om);
    if (Array.isArray(r3.data) && r3.data.length > 0) {
      setColourMap(r3.data.map((c) => ({ butt: c.butt_mm, colour: c.colour, swatch: c.swatch || "#888888" })));
    }
  };
  useEffect(() => {
    // eslint-disable-next-line
    load();
  }, []);

  const filtered = records.filter((r) => !q || JSON.stringify(r).toLowerCase().includes(q.toLowerCase()));

  const swatchFor = (name) => colourMap.find((x) => x.colour === name)?.swatch || "#cbd5e1";

  // Group the Awaiting-Pointing poles per order/job so they can be pointed batch-by-batch.
  const pointGroups = (() => {
    const acc = {};
    for (const s of awaitingPointing) {
      const key = s.job_ref || s.order_number || s.order_id || "— (no order)";
      acc[key] = acc[key] || { key, order_number: s.order_number, job_ref: s.job_ref, note: s.finishing_note, ids: [], qty: 0, specs: {} };
      acc[key].ids.push(s.id);
      acc[key].qty += s.quantity || 1;
      const spec = `${s.colour || "—"}|${s.butt_size ?? "—"}|${s.length ?? "—"}`;
      acc[key].specs[spec] = acc[key].specs[spec] || {
        colour: s.colour, butt_size: s.butt_size, length: s.length, qty: 0, ids: [],
      };
      acc[key].specs[spec].qty += s.quantity || 1;
      acc[key].specs[spec].ids.push(s.id);
    }
    return Object.values(acc).sort((a, b) => String(a.key).localeCompare(String(b.key)));
  })();

  const [pointProg, setPointProg] = useState({});

  const recordPointProgress = async (g) => {
    const items = Object.entries(g.specs)
      .map(([spec, sp]) => ({ stock_item_ids: sp.ids, qty: Math.floor(Number(pointProg[`${g.key}|${spec}`] || 0)) }))
      .filter((it) => it.qty > 0);
    if (!items.length) { toast.error("Enter a pointed quantity on at least one size line"); return; }
    try {
      const r = await api.post("/stock/point-progress", { items });
      toast.success(`Recorded: ${r.data.pointed} pole(s) pointed`);
      setPointProg((p) => {
        const c = { ...p };
        Object.keys(c).forEach((k) => { if (k.startsWith(`${g.key}|`)) delete c[k]; });
        return c;
      });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const markPointed = async (ids) => {
    try {
      const r = await api.post("/stock/mark-pointed", { stock_item_ids: ids, pointed: true });
      toast.success(`Marked ${r.data.pointed} pole(s) pointed`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  // Group Awaiting-Bundling poles per order/job AND per size — only identical
  // specs (colour · butt Ø · length) may go into the same bundle.
  const bundleGroups = (() => {
    const acc = {};
    for (const s of awaitingBundling) {
      const job = s.job_ref || s.order_number || s.order_id || "— (no order)";
      const key = `${job}|${s.colour || ""}|${s.butt_size ?? ""}|${s.length ?? ""}`;
      acc[key] = acc[key] || { key, job, order_number: s.order_number, job_ref: s.job_ref, note: s.finishing_note, bundle_size: s.bundle_size, colour: s.colour, butt_size: s.butt_size, length: s.length, ids: [], qty: 0 };
      acc[key].ids.push(s.id);
      acc[key].qty += s.quantity || 1;
    }
    return Object.values(acc).map((g) => {
      const o = ordersMap[g.order_number];
      const line = o?.line_items?.find((l) =>
        Number(l.butt_size) === Number(g.butt_size) && Number(l.length) === Number(g.length)
        && (!l.colour || !g.colour || l.colour === g.colour));
      return { ...g, ppb: line?.poles_per_bundle || o?.poles_per_bundle || g.bundle_size || null };
    }).sort((a, b) => String(a.key).localeCompare(String(b.key)));
  })();

  const [bundleCounts, setBundleCounts] = useState({});

  const removeFinishing = async (type, group) => {
    if (!window.confirm(`Remove this ${type} job from the queue? ${group.ids.length} pole(s) will no longer wait for ${type}. The poles themselves are not changed.`)) return;
    try {
      const r = await api.post("/stock/cancel-finishing", { stock_item_ids: group.ids, type });
      toast.success(`${type === "bundling" ? "Bundling" : "Pointing"} job removed — ${r.data.removed} pole(s) released from the queue`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const createBundle = async (group, count = 1) => {
    const hasPpb = group.ppb && group.ppb > 0;
    try {
      const r = await api.post("/stock/mark-bundled", {
        stock_item_ids: group.ids,
        bundle_size: hasPpb ? group.ppb : null,
        bundle_count: hasPpb ? Math.max(1, Number(count) || 1) : null,
      });
      const made = r.data.bundles?.length || 1;
      const remaining = group.qty - r.data.bundled;
      toast.success(`Created ${made} bundle(s) — ${r.data.bundled} poles${remaining > 0 ? ` · ${remaining} pole(s) still awaiting bundling` : " · job complete"}`);
      setBundleCounts((c) => ({ ...c, [group.key]: "" }));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  // Map each awaiting-paint stock item to its child receiving row so we can show
  // which receipt (Delivery Note) it came from. Group by DN + spec so each card
  // corresponds to one receipt + spec — clear even with many poles in the queue.
  const recvById = React.useMemo(() => {
    const m = {};
    for (const r of recvAll) m[r.id] = r;
    return m;
  }, [recvAll]);

  const paintGroups = (() => {
    const acc = {};
    for (const s of awaitingPaint) {
      const rec = recvById[s.receiving_id] || {};
      const dn = rec.delivery_note_number || "— (no DN)";
      const key = dn;
      acc[key] = acc[key] || {
        key, dn, date: rec.date_received, order_number: rec.order_number,
        ids: [], qty: 0, specs: {},
      };
      acc[key].ids.push(s.id);
      acc[key].qty += s.quantity || 1;
      const spec = `${s.colour || "—"}|${s.butt_size ?? "—"}|${s.length ?? "—"}`;
      acc[key].specs[spec] = acc[key].specs[spec] || {
        colour: s.colour, butt_size: s.butt_size, length: s.length, qty: 0, ids: [],
      };
      acc[key].specs[spec].qty += s.quantity || 1;
      acc[key].specs[spec].ids.push(s.id);
    }
    return Object.values(acc).sort((a, b) => (a.dn || "").localeCompare(b.dn || ""));
  })();

  // Download a printable job sheet PDF (paint / pointing / bundling) for staff.
  const downloadJobSheet = async (type, ids) => {
    try {
      const r = await api.post("/processing/job-sheet.pdf", { type, stock_item_ids: ids }, { responseType: "blob" });
      saveBlob(r, `${type}-job-sheet.pdf`);
    } catch (e) {
      toast.error("Failed to generate job sheet");
    }
  };

  // Reprint a paint job by delivery note: scope 'full' (all poles incl. painted)
  // or 'remaining' (only what still needs painting).
  const downloadPaintJobByDn = async (dn, scope) => {
    try {
      const r = await api.post("/processing/job-sheet.pdf",
        { type: "paint", delivery_note: dn, scope }, { responseType: "blob" });
      saveBlob(r, `paint-job-${dn}-${scope}.pdf`);
    } catch (e) {
      let msg = "Failed to generate job sheet";
      if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* noop */ }
      }
      toast.error(msg);
    }
  };

  const [showPaintHistory, setShowPaintHistory] = useState(false);
  const [paintJobs, setPaintJobs] = useState(null);
  const loadPaintJobs = async () => {
    try {
      const r = await api.get("/processing/paint-jobs");
      setPaintJobs(r.data);
    } catch (e) {
      toast.error("Failed to load paint job history");
    }
  };
  const togglePaintHistory = () => {
    const next = !showPaintHistory;
    setShowPaintHistory(next);
    if (next) loadPaintJobs();
  };


  const markPainted = async (ids) => {
    try {
      const r = await api.post("/processing/mark-painted", { stock_item_ids: ids });
      toast.success(`Marked ${r.data.painted} pole(s) painted → available`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  // Mark a specific quantity of a single spec line painted (partial release).
  const markPaintedLine = async (lineKey, sp) => {
    const raw = paintQty[lineKey];
    const n = raw === undefined || raw === "" ? sp.qty : Math.floor(Number(raw));
    if (!n || n < 1) { toast.error("Enter a quantity of 1 or more"); return; }
    if (n > sp.ids.length) { toast.error(`Only ${sp.ids.length} pole(s) available on this line`); return; }
    await markPainted(sp.ids.slice(0, n));
    setPaintQty((p) => { const c = { ...p }; delete c[lineKey]; return c; });
  };

  const loadCutReport = async () => {
    setCrLoading(true);
    try {
      const r = await api.get("/processing/cut-report", { params: { date_from: crFrom, date_to: crTo } });
      setCutReport(r.data);
    } catch (e) {
      toast.error("Could not load the cutting report");
    }
    setCrLoading(false);
  };
  const openCutReport = () => { setCutReportOpen(true); setCutReport(null); loadCutReport(); };
  const downloadCutReport = async (fmt) => {
    try {
      const r = await api.get(`/processing/cut-report.${fmt}`, {
        params: { date_from: crFrom, date_to: crTo }, responseType: "blob",
      });
      saveBlob(r, `cut-report-${crFrom}-to-${crTo}.${fmt}`);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const downloadCutListSheet = async (plan) => {
    try {
      const r = await api.get(`/processing/cut-list/${plan.id}/sheet.pdf`, { responseType: "blob" });
      saveBlob(r, `cut-list-${plan.cut_ref}.pdf`);
    } catch (e) {
      toast.error("Failed to generate cut list sheet");
    }
  };

  const toggleCombine = (id) =>
    setCombineSel((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const openCombineModal = () => {
    const plans = cutList.filter((p) => combineSel.includes(p.id));
    const seen = {};
    const items = [];
    plans.forEach((p) => {
      const lines = p.plan_lines?.length
        ? p.plan_lines.map((pl) => ({ ...pl.spec, qty: pl.to_cut || 0 }))
        : (p.paint_lines || []).map((l) => ({ length: l.length, butt_size: l.butt_size, colour: l.colour, qty: l.quantity || 0 }));
      lines.forEach((sp) => {
        const k = `${sp.length}|${sp.butt_size}|${(sp.colour || "").toUpperCase()}`;
        if (seen[k]) { seen[k].qty += sp.qty; seen[k].refs.add(p.cut_ref); return; }
        const it = { length: sp.length, butt_size: sp.butt_size, colour: sp.colour, qty: sp.qty, refs: new Set([p.cut_ref]) };
        seen[k] = it;
        items.push(it);
      });
    });
    setCombineItems(items);
  };

  const moveCombineItem = (i, dir) =>
    setCombineItems((p) => {
      const c = [...p];
      const j = i + dir;
      if (j < 0 || j >= c.length) return p;
      [c[i], c[j]] = [c[j], c[i]];
      return c;
    });

  const downloadCombined = async () => {
    try {
      const r = await api.post("/processing/cut-list/combined.pdf", {
        plan_ids: combineSel,
        priority: combineItems.map((it) => ({ length: it.length, butt_size: it.butt_size, colour: it.colour })),
      }, { responseType: "blob" });
      saveBlob(r, "cut-list-combined.pdf");
      setCombineItems(null);
    } catch (e) {
      toast.error("Failed to generate combined cut list");
    }
  };

  // Per-line comment editor on applied cut plans (prints on the cut list sheet).
  const [cutNotes, setCutNotes] = useState({});
  const noteLines = (plan) => (plan.plan_lines?.length
    ? plan.plan_lines.map((pl) => ({ length: pl.spec?.length, butt_size: pl.spec?.butt_size, colour: pl.spec?.colour, note: pl.spec?.note }))
    : (plan.request_lines || []).map((r) => ({ length: r.length, butt_size: r.butt_size, colour: r.colour, note: r.note })));

  const saveCutNotes = async (plan) => {
    const items = noteLines(plan).map((ln, i) => ({
      length: ln.length, butt_size: ln.butt_size,
      note: (cutNotes[`${plan.id}|${i}`] ?? (ln.note || "")).trim(),
    }));
    try {
      await api.post(`/processing/cut-list/${plan.id}/notes`, { items });
      toast.success("Line comments saved — they print on the Cut List Sheet PDF");
      setCutNotes((p) => {
        const c = { ...p };
        Object.keys(c).forEach((k) => { if (k.startsWith(`${plan.id}|`)) delete c[k]; });
        return c;
      });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const cancelCutPlan = async (plan) => {
    if (!window.confirm(`Cancel cut plan ${plan.cut_ref}? Its uncut donor poles are restored to Available and the awaiting-paint pieces are removed.`)) return;
    try {
      await api.post(`/orders/cut-plans/${plan.id}/cancel`);
      toast.success("Cut plan cancelled — donor poles restored to Available");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const editCutPlan = async (plan) => {
    if (!window.confirm(`Edit ${plan.cut_ref}? The plan is cancelled first (donors restored), then the setup reopens with its sizes so you can adjust and re-apply.`)) return;
    try {
      await api.post(`/orders/cut-plans/${plan.id}/cancel`);
      toast.success("Plan cancelled — adjust the sizes and re-apply");
      load();
      setStockCutOpen({ specs: plan.request_lines });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const revertGroup = async (g) => {
    if (!window.confirm(`Send ${g.qty} pole(s) (DN ${g.dn}) back to Processing? The original receipt quantity will be restored so you can re-process them.`)) return;
    try {
      const r = await api.post("/processing/revert", { stock_item_ids: g.ids });
      toast.success(`Reverted ${r.data.reverted} pole(s) → back to pending processing`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  // Record shop-floor progress (donor poles cut / finished poles painted today).
  const reduceLine = async (plan, ln, i) => {
    const qty = Math.floor(Number(reduceQty[`${plan.id}|r${i}`] || 0));
    if (!qty) { toast.error("Enter how many donor poles to release"); return; }
    try {
      const r = await api.post(`/processing/cut-list/${plan.id}/reduce`, {
        length: ln.length, butt_size: ln.butt_size, colour: ln.colour, qty,
      });
      toast.success(`Released ${r.data.released_donors} donor pole(s) back to stock — ${r.data.pieces_removed} planned piece(s) removed${r.data.plan_closed ? ". Plan closed." : ""}`);
      setReduceQty((p) => ({ ...p, [`${plan.id}|r${i}`]: "" }));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const recordCutProgress = async (plan) => {
    const items = [
      ...plan.donor_lines.map((ln, i) => ({
        type: "cut", length: ln.length, butt_size: ln.butt_size, colour: ln.colour,
        qty: Math.floor(Number(cutProg[`${plan.id}|d${i}`] || 0)),
      })),
      ...plan.paint_lines.map((ln, i) => ({
        type: "paint", length: ln.length, butt_size: ln.butt_size, colour: ln.colour,
        qty: Math.floor(Number(cutProg[`${plan.id}|p${i}`] || 0)),
      })),
    ].filter((it) => it.qty > 0);
    if (!items.length) { toast.error("Enter a cut or painted quantity on at least one line"); return; }
    try {
      const r = await api.post(`/processing/cut-list/${plan.id}/progress`, { items });
      toast.success(`Recorded: ${r.data.cut} cut, ${r.data.painted} painted${r.data.completed ? " — cut plan complete!" : ""}`);
      setCutProg((p) => {
        const c = { ...p };
        Object.keys(c).forEach((k) => { if (k.startsWith(`${plan.id}|`)) delete c[k]; });
        return c;
      });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <AppLayout>
      <PageHeader
        title="Processing"
        subtitle="Measure poles in batches, auto-assign paint colours by diameter, paint, then release to available stock."
      />

      <div className="panel p-3 mb-4">
        <div className="data-label mb-2 flex items-center gap-2"><Palette className="h-3.5 w-3.5" /> Paint Colour Reference (by Butt Diameter)</div>
        <div className="flex flex-wrap gap-2">
          {colourMap.map((c) => (
            <div key={`${c.butt}-${c.colour}`} className="flex items-center gap-2 px-2 py-1 rounded-sm border border-stone-200 bg-white text-xs">
              <span className="inline-block h-3.5 w-3.5 rounded-full border border-stone-300" style={{ background: c.swatch }} />
              <span className="font-mono text-stone-700">{c.butt} mm</span>
              <span className="text-stone-500">·</span>
              <span className="font-semibold uppercase tracking-wider">{c.colour}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="panel p-4 mb-6">
        <div className="font-display text-base font-semibold mb-2">Pending Processing ({pending.length})</div>
        {pending.length === 0 ? (
          <div className="text-sm text-stone-400 py-4">No raw receipts waiting to be processed.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {pending.map((r) => (
              <button
                key={r.id}
                onClick={() => canCreate && setTarget(r)}
                className="text-left panel p-3 hover:bg-stone-50 transition-colors"
                data-testid={`pending-${r.id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="font-mono text-xs text-stone-500">{r.date_received}</div>
                  <div className="text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-sm font-bold tracking-wider">RAW</div>
                </div>
                <div className="font-semibold">{r.delivery_note_number || "—"}</div>
                <div className="text-xs text-stone-500">{r.supplier_name || r.origin} · {r.transporter || "—"}</div>
                <div className="mt-2 flex items-center justify-between">
                  <div className="font-mono text-sm">
                    {fmtNumber((r.quantity || 0) - (r.processed_quantity || 0))} <span className="text-stone-400">/ {fmtNumber(r.quantity)}</span> pcs
                  </div>
                  {canCreate && <span className="text-xs text-emerald-800 font-medium">Process →</span>}
                </div>
                {(r.processed_quantity || 0) > 0 && (
                  <div className="text-[11px] text-stone-500 mt-0.5 font-mono">
                    {fmtNumber(r.processed_quantity)} processed · {fmtNumber((r.quantity || 0) - (r.processed_quantity || 0))} remaining
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="panel p-4 mb-6 border-amber-200">
        <div className="font-display text-base font-semibold flex items-center justify-between gap-2 text-amber-800">
          <button onClick={() => setDmgOpen((v) => !v)} className="flex items-center gap-2 hover:opacity-75"
                  data-testid="damaged-toggle">
            <AlertTriangle className="h-4 w-4" /> Damaged — Awaiting Salvage ({damaged.length})
            <span className="text-xs font-normal bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-sm font-mono">
              {damaged.reduce((a, s) => a + (Number(s.quantity) || 1), 0)} poles · awaiting customer feedback
            </span>
            <ChevronRight className={`h-4 w-4 transition-transform ${dmgOpen ? "rotate-90" : ""}`} />
          </button>
          {dmgOpen && damaged.length > 0 && (
            <Btn variant="secondary" size="sm" onClick={() => downloadJobSheet("salvage", damaged.map((s) => s.id))} testid="salvage-jobsheet">
              <FileText className="h-3.5 w-3.5" /> Job Sheet (PDF)
            </Btn>
          )}
        </div>
        {dmgOpen && (damaged.length === 0 ? (
          <div className="text-sm text-stone-400 py-4">No damaged poles awaiting salvage.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2" data-testid="awaiting-salvage">
            {damaged.map((s) => (
              <button
                key={s.id}
                onClick={() => canCreate && setSalvageTarget(s)}
                className="text-left panel p-3 hover:bg-amber-50 transition-colors border-amber-200"
                data-testid={`salvage-${s.id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="font-mono text-xs text-stone-500">{s.bundle_number || s.id.slice(0, 8)}</div>
                  <div className="text-xs bg-red-100 text-red-800 px-1.5 py-0.5 rounded-sm font-bold tracking-wider">DAMAGED</div>
                </div>
                <div className="font-semibold text-sm">{s.product_name || "—"}</div>
                <div className="text-xs text-stone-500 font-mono">
                  {s.butt_size != null ? `${s.butt_size}mm` : "—"} · {s.length ? `${s.length}m` : "—"} · {s.colour || "—"}
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <div className="font-mono text-[11px] text-stone-500">
                    DN {s.delivery_note_number || "—"} · WB {s.weighbridge_number || "—"}
                  </div>
                  {canCreate && <span className="text-xs text-amber-800 font-medium">Salvage →</span>}
                </div>
              </button>
            ))}
          </div>
        ))}
      </div>

      <div className="panel p-4 mb-6 border-fuchsia-200">
        <div className="font-display text-base font-semibold mb-2 flex items-center gap-2 text-fuchsia-800">
          <PaintBucket className="h-4 w-4" /> Awaiting Paint ({awaitingPaint.length})
        </div>
        {paintGroups.length === 0 ? (
          <div className="text-sm text-stone-400 py-4">No processed poles waiting to be painted.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="awaiting-paint">
            {paintGroups.map((g) => (
              <div key={g.key} className="panel p-3 border-fuchsia-200" data-testid={`paint-group-${g.dn}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-sm">DN <span className="font-mono text-stone-800">{g.dn}</span></span>
                  <div className="text-xs bg-fuchsia-100 text-fuchsia-800 px-1.5 py-0.5 rounded-sm font-bold">{g.qty} pcs</div>
                </div>
                <div className="text-xs text-stone-500 mb-2">
                  {g.date && <span>{g.date}</span>}
                  {g.order_number && <span> · Order {g.order_number}</span>}
                </div>
                <div className="text-xs text-stone-600 mb-2 space-y-1">
                  {Object.values(g.specs).map((sp, i) => {
                    const lineKey = `${g.key}|${i}`;
                    return (
                    <div key={i} className="flex items-center gap-1.5 font-mono">
                      <span className="inline-block h-3 w-3 rounded-full border border-stone-300 shrink-0" style={{ background: swatchFor(sp.colour) }} />
                      <span className="font-semibold">{sp.colour || "—"}</span>
                      <span className="text-stone-500">· {sp.butt_size != null ? `${sp.butt_size}mm` : "—"} · {sp.length ? `${sp.length}m` : "—"}</span>
                      <span className="ml-auto font-semibold text-stone-700">{sp.qty}×</span>
                      {canCreate && (
                        <div className="ml-1.5 flex items-center gap-1">
                          <input
                            type="number" min="1" max={sp.ids.length}
                            value={paintQty[lineKey] ?? ""}
                            placeholder={String(sp.qty)}
                            onChange={(e) => setPaintQty((p) => ({ ...p, [lineKey]: e.target.value }))}
                            title="Quantity to mark painted (blank = all)"
                            className="w-12 rounded-sm border border-stone-300 px-1 py-0.5 text-[11px] text-right"
                            data-testid={`paint-qty-${g.dn}-${i}`}
                          />
                          <button
                            onClick={() => markPaintedLine(lineKey, sp)}
                            title="Mark this quantity painted → available"
                            className="inline-flex items-center gap-1 rounded-sm border border-fuchsia-300 bg-fuchsia-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-fuchsia-800 hover:bg-fuchsia-100 transition-colors"
                            data-testid={`mark-painted-line-${g.dn}-${i}`}
                          >
                            <PaintBucket className="h-3 w-3" /> Painted
                          </button>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
                <div className="flex flex-col gap-1.5">
                  <Btn variant="secondary" className="w-full" onClick={() => downloadJobSheet("paint", g.ids)} testid={`paint-jobsheet-${g.dn}`}>
                    <FileText className="h-3.5 w-3.5" /> Job Sheet — Still to Paint (PDF)
                  </Btn>
                  <Btn variant="secondary" className="w-full" onClick={() => downloadPaintJobByDn(g.dn, "full")} testid={`paint-jobsheet-full-${g.dn}`}>
                    <FileText className="h-3.5 w-3.5" /> Job Sheet — Full Job (PDF)
                  </Btn>
                  {canCreate && (
                    <>
                      <Btn className="w-full" onClick={() => markPainted(g.ids)} testid={`mark-painted-${g.dn}`}>
                        <PaintBucket className="h-3.5 w-3.5" /> Mark All Painted
                      </Btn>
                      <Btn variant="secondary" className="w-full" onClick={() => revertGroup(g)} testid={`revert-paint-${g.dn}`}>
                        <RotateCcw className="h-3.5 w-3.5" /> Back to Processing
                      </Btn>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cut List — cut-plan poles tracked per plan, not per receipt */}
      <div className="panel p-4 mb-6 border-teal-200" data-testid="cut-list-panel">
        <div className="font-display text-base font-semibold mb-2 flex items-center gap-2 text-teal-800">
          <Scissors className="h-4 w-4" /> Cut List ({cutList.length})
          <div className="ml-auto flex items-center gap-1.5">
            {combineSel.length >= 2 && (
              <Btn size="sm" onClick={openCombineModal} testid="combine-cut-lists-btn">
                <Layers className="h-3.5 w-3.5" /> Combined Sheet ({combineSel.length})
              </Btn>
            )}
            <Btn size="sm" variant="secondary" onClick={openCutReport} testid="cut-report-btn">
              <History className="h-3.5 w-3.5" /> Cut Report
            </Btn>
            {canCreate && (
              <Btn size="sm" variant="secondary" onClick={() => setStockCutOpen(true)}
                   testid="new-stock-cut-plan-btn">
                <Plus className="h-3.5 w-3.5" /> Stock Cut Plan
              </Btn>
            )}
          </div>
        </div>
        {cutList.length === 0 ? (
          <div className="text-sm text-stone-400 py-4">
            No active cut plans. Apply a cut plan from an order (Orders → expand → Cut Plan), or set one up here
            with “Stock Cut Plan” to cut for stock without an order.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {cutList.map((plan) => (
              <div key={plan.id} className="panel p-3 border-teal-200" data-testid={`cut-plan-card-${plan.cut_ref}`}>
                <div className="flex items-center justify-between mb-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={combineSel.includes(plan.id)}
                           onChange={() => toggleCombine(plan.id)}
                           title="Select to combine into one cut list sheet"
                           data-testid={`combine-select-${plan.cut_ref}`} />
                    <span className="font-mono font-semibold text-sm">{plan.cut_ref}</span>
                  </label>
                  <div className="text-xs bg-teal-100 text-teal-800 px-1.5 py-0.5 rounded-sm font-bold">
                    Cut {plan.totals.donors_cut}/{plan.totals.donors} · Painted {plan.totals.painted}/{plan.totals.poles}
                  </div>
                </div>
                <div className="text-xs text-stone-500 mb-2">
                  Order {plan.order_number} · applied {(plan.applied_at || "").slice(0, 10)}
                </div>

                <div className="text-[11px] font-semibold uppercase tracking-wider text-teal-800 mb-1">1 · Cut — donor poles</div>
                <div className="text-xs text-stone-600 space-y-1 mb-3">
                  {plan.donor_lines.map((ln, i) => (
                    <div key={i} className="flex items-center gap-1.5 font-mono flex-wrap" data-testid={`donor-line-${plan.cut_ref}-${i}`}>
                      <span className="inline-block h-3 w-3 rounded-full border border-stone-300 shrink-0" style={{ background: swatchFor(ln.colour) }} />
                      <span className="font-semibold">{ln.colour || "—"}</span>
                      <span className="text-stone-500">· {ln.butt_size != null ? `${ln.butt_size}mm` : "—"} · {ln.length ? `${ln.length}m` : "—"}</span>
                      <span className="font-semibold text-stone-700">{ln.quantity}×</span>
                      <span className={`text-[11px] px-1 rounded-sm ${ln.done_qty >= ln.quantity ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-stone-600"}`}>
                        cut {ln.done_qty}/{ln.quantity}
                      </span>
                      {canCreate && (
                        <input type="number" min="0" max={ln.quantity - ln.done_qty}
                               value={cutProg[`${plan.id}|d${i}`] ?? ""}
                               onChange={(e) => setCutProg((p) => ({ ...p, [`${plan.id}|d${i}`]: e.target.value }))}
                               placeholder="cut today" title="Donor poles cut today on this line"
                               className="ml-auto w-20 rounded-sm border border-stone-300 px-1 py-0.5 text-[11px] text-right"
                               data-testid={`cut-input-${plan.cut_ref}-${i}`} />
                      )}
                      {canCreate && ln.quantity - ln.done_qty > 0 && (
                        <span className="flex items-center gap-1 basis-full justify-end">
                          <input type="number" min="0" max={ln.quantity - ln.done_qty}
                                 value={reduceQty[`${plan.id}|r${i}`] ?? ""}
                                 onChange={(e) => setReduceQty((p) => ({ ...p, [`${plan.id}|r${i}`]: e.target.value }))}
                                 placeholder="arrived pre-cut"
                                 title="Stock arrived already at size — release this many uncut donor poles back to Available"
                                 className="w-24 rounded-sm border border-amber-300 px-1 py-0.5 text-[11px] text-right"
                                 data-testid={`reduce-input-${plan.cut_ref}-${i}`} />
                          <button onClick={() => reduceLine(plan, ln, i)}
                                  className="text-[11px] font-semibold text-amber-700 hover:underline"
                                  data-testid={`reduce-btn-${plan.cut_ref}-${i}`}>
                            Release
                          </button>
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                <div className="text-[11px] font-semibold uppercase tracking-wider text-fuchsia-800 mb-1">2 · Paint — finished poles</div>
                <div className="text-xs text-stone-600 space-y-1 mb-2">
                  {plan.paint_lines.map((ln, i) => (
                    <div key={i} className="flex items-center gap-1.5 font-mono flex-wrap" data-testid={`paint-line-${plan.cut_ref}-${i}`}>
                      <span className="inline-block h-3 w-3 rounded-full border border-stone-300 shrink-0" style={{ background: swatchFor(ln.colour) }} />
                      <span className="font-semibold">{ln.colour || "—"}</span>
                      <span className="text-stone-500">· {ln.butt_size != null ? `${ln.butt_size}mm` : "—"} · {ln.length ? `${ln.length}m` : "—"}</span>
                      <span className="font-semibold text-stone-700">{ln.quantity}×</span>
                      <span className={`text-[11px] px-1 rounded-sm ${ln.done_qty >= ln.quantity ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-stone-600"}`}>
                        painted {ln.done_qty}/{ln.quantity}
                      </span>
                      {canCreate && (
                        <input type="number" min="0" max={ln.quantity - ln.done_qty}
                               value={cutProg[`${plan.id}|p${i}`] ?? ""}
                               onChange={(e) => setCutProg((p) => ({ ...p, [`${plan.id}|p${i}`]: e.target.value }))}
                               placeholder="painted today" title="Poles painted today on this line (painted poles become available stock)"
                               className="ml-auto w-24 rounded-sm border border-stone-300 px-1 py-0.5 text-[11px] text-right"
                               data-testid={`painted-input-${plan.cut_ref}-${i}`} />
                      )}
                    </div>
                  ))}
                </div>

                {canCreate && noteLines(plan).length > 0 && (
                  <div className="mb-2" data-testid={`cut-notes-${plan.cut_ref}`}>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-sky-800 mb-1">
                      Line comments — printed on the cut list sheet
                    </div>
                    <div className="space-y-1">
                      {noteLines(plan).map((ln, i) => (
                        <div key={i} className="flex items-center gap-1.5 text-xs">
                          <span className="font-mono text-stone-600 shrink-0 w-28">
                            {ln.length != null ? `${ln.length}m` : "—"} ⌀{ln.butt_size != null ? `${ln.butt_size}mm` : "—"}
                          </span>
                          <input
                            className="flex-1 rounded-sm border border-stone-300 px-1.5 py-0.5 text-[11px]"
                            placeholder="e.g. V-POINT / 10 per bundle"
                            value={cutNotes[`${plan.id}|${i}`] ?? (ln.note || "")}
                            onChange={(e) => setCutNotes((p) => ({ ...p, [`${plan.id}|${i}`]: e.target.value }))}
                            data-testid={`cut-note-input-${plan.cut_ref}-${i}`}
                          />
                        </div>
                      ))}
                      {Object.keys(cutNotes).some((k) => k.startsWith(`${plan.id}|`)) && (
                        <button
                          onClick={() => saveCutNotes(plan)}
                          className="text-[11px] font-semibold uppercase tracking-wider text-white bg-sky-700 hover:bg-sky-800 rounded-sm px-2 py-1"
                          data-testid={`save-cut-notes-${plan.cut_ref}`}
                        >
                          Save Comments
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-1.5">
                  {canCreate && (
                    <Btn className="w-full" onClick={() => recordCutProgress(plan)} testid={`record-progress-${plan.cut_ref}`}>
                      <Scissors className="h-3.5 w-3.5" /> Record Today's Progress
                    </Btn>
                  )}
                  <Btn variant="secondary" className="w-full" onClick={() => downloadCutListSheet(plan)} testid={`cut-list-sheet-${plan.cut_ref}`}>
                    <FileText className="h-3.5 w-3.5" /> Cut List Sheet (PDF)
                  </Btn>
                  {canCreate && plan.cancellable && (
                    <div className="flex gap-1.5">
                      {plan.order_number === "STOCK" && (plan.request_lines || []).length > 0 && (
                        <Btn variant="secondary" className="flex-1" onClick={() => editCutPlan(plan)}
                             testid={`edit-cut-plan-${plan.cut_ref}`}>
                          Edit Plan
                        </Btn>
                      )}
                      <Btn variant="danger" className="flex-1" onClick={() => cancelCutPlan(plan)}
                           testid={`cancel-cut-plan-${plan.cut_ref}`}>
                        Cancel Plan
                      </Btn>
                    </div>
                  )}
                  {canCreate && !plan.cancellable && (
                    <div className="text-[11px] text-stone-400" data-testid={`cut-plan-locked-${plan.cut_ref}`}>
                      Progress has been recorded — this plan can no longer be cancelled or edited.
                    </div>
                  )}
                </div>
                <div className="text-[11px] text-stone-500 mt-1.5">
                  Painted poles move to Available stock. The plan completes automatically once every pole is painted.
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Paint Job History — reprint any historical paint job (full or remaining) */}
      <div className="panel p-4 mb-6 border-fuchsia-200" data-testid="paint-history-panel">
        <button onClick={togglePaintHistory} className="w-full flex items-center gap-2 text-left font-display text-base font-semibold text-fuchsia-800" data-testid="paint-history-toggle">
          <ChevronRight className={`h-4 w-4 transition-transform ${showPaintHistory ? "rotate-90" : ""}`} />
          <History className="h-4 w-4" /> Paint Job History — Reprint
        </button>
        {showPaintHistory && (
          !paintJobs ? (
            <div className="text-sm text-stone-400 py-4">Loading paint jobs…</div>
          ) : paintJobs.length === 0 ? (
            <div className="text-sm text-stone-400 py-4">No paint jobs found yet.</div>
          ) : (
            <div className="overflow-x-auto mt-3">
              <table className="min-w-full text-sm" data-testid="paint-history-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-stone-500 border-b border-stone-200">
                    <th className="px-3 py-2">DN #</th>
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Weighbridge #</th>
                    <th className="px-3 py-2 text-right">Total Poles</th>
                    <th className="px-3 py-2 text-right">Painted</th>
                    <th className="px-3 py-2 text-right">Still to Paint</th>
                    <th className="px-3 py-2 text-right">Print</th>
                  </tr>
                </thead>
                <tbody>
                  {paintJobs.map((j) => (
                    <tr key={j.delivery_note} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`paint-history-row-${j.delivery_note}`}>
                      <td className="px-3 py-2 font-mono font-semibold">{j.delivery_note}</td>
                      <td className="px-3 py-2 font-mono">{j.date || "—"}</td>
                      <td className="px-3 py-2 font-mono">{j.weighbridge_number || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtNumber(j.total, 0)}</td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-800">{fmtNumber(j.painted, 0)}</td>
                      <td className="px-3 py-2 text-right font-mono text-fuchsia-800 font-bold">{fmtNumber(j.remaining, 0)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <Btn variant="secondary" onClick={() => downloadPaintJobByDn(j.delivery_note, "full")} testid={`paint-history-full-${j.delivery_note}`}>
                          <FileText className="h-3.5 w-3.5" /> Full Job
                        </Btn>{" "}
                        <Btn variant="secondary" disabled={!j.remaining} onClick={() => downloadPaintJobByDn(j.delivery_note, "remaining")} testid={`paint-history-remaining-${j.delivery_note}`}>
                          <FileText className="h-3.5 w-3.5" /> Still to Paint
                        </Btn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      <div className="panel p-4 mb-6 border-sky-200">
        <div className="font-display text-base font-semibold mb-2 flex items-center gap-2 text-sky-800">
          <Triangle className="h-4 w-4" /> Awaiting Pointing ({awaitingPointing.length})
        </div>
        {pointGroups.length === 0 ? (
          <div className="text-sm text-stone-400 py-4">No allocated poles awaiting pointing. Orders flagged “Requires Pointing” will queue their allocated poles here.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="awaiting-pointing">
            {pointGroups.map((g) => (
              <div key={g.key} className="panel p-3 border-sky-200" data-testid={`point-group-${g.order_number || g.key}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-sm">{g.order_number ? `Order ${g.order_number}` : (g.job_ref || "—")}</span>
                  <div className="text-xs bg-sky-100 text-sky-800 px-1.5 py-0.5 rounded-sm font-bold">{g.qty} pcs</div>
                </div>
                {g.note && <div className="text-xs text-stone-600 italic mb-1">“{g.note}”</div>}
                <div className="text-xs text-stone-600 mb-2 space-y-1">
                  {Object.entries(g.specs).map(([spec, sp]) => (
                    <div key={spec} className="flex items-center gap-1.5 font-mono flex-wrap" data-testid={`point-spec-${g.order_number || g.key}-${spec}`}>
                      <span className="inline-block h-3 w-3 rounded-full border border-stone-300 shrink-0" style={{ background: swatchFor(sp.colour) }} />
                      <span className="font-semibold">{sp.colour || "—"}</span>
                      <span className="text-stone-500">· {sp.butt_size != null ? `${sp.butt_size}mm` : "—"} · {sp.length != null ? `${sp.length}m` : "—"}</span>
                      <span className="font-semibold text-stone-700">{sp.qty}×</span>
                      {canCreate && (
                        <input type="number" min="0" max={sp.qty}
                               value={pointProg[`${g.key}|${spec}`] ?? ""}
                               onChange={(e) => setPointProg((p) => ({ ...p, [`${g.key}|${spec}`]: e.target.value }))}
                               placeholder="pointed today" title="Poles pointed today on this size line"
                               className="ml-auto w-24 rounded-sm border border-stone-300 px-1 py-0.5 text-[11px] text-right"
                               data-testid={`point-input-${g.order_number || g.key}-${spec}`} />
                      )}
                    </div>
                  ))}
                </div>
                {canCreate && (
                  <div className="flex flex-col gap-1.5">
                    <Btn className="w-full" onClick={() => recordPointProgress(g)} testid={`record-pointed-${g.order_number || g.key}`}>
                      <Triangle className="h-3.5 w-3.5" /> Record Today's Pointing
                    </Btn>
                    <Btn variant="secondary" className="w-full" onClick={() => downloadJobSheet("pointing", g.ids)} testid={`point-jobsheet-${g.order_number || g.key}`}>
                      <FileText className="h-3.5 w-3.5" /> Download Job Sheet (PDF)
                    </Btn>
                    <Btn variant="secondary" className="w-full" onClick={() => markPointed(g.ids)} testid={`mark-pointed-${g.order_number || g.key}`}>
                      <Triangle className="h-3.5 w-3.5" /> Mark All Pointed
                    </Btn>
                    <Btn variant="secondary" className="w-full text-red-700 hover:bg-red-50" onClick={() => removeFinishing("pointing", g)} testid={`remove-point-job-${g.order_number || g.key}`}>
                      <Trash2 className="h-3.5 w-3.5" /> Remove Job
                    </Btn>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="panel p-4 mb-6 border-indigo-200">
        <div className="font-display text-base font-semibold mb-2 flex items-center gap-2 text-indigo-800">
          <Package className="h-4 w-4" /> Awaiting Bundling ({awaitingBundling.reduce((a, s) => a + (s.quantity || 1), 0)} poles)
        </div>
        {bundleGroups.length === 0 ? (
          <div className="text-sm text-stone-400 py-4">No allocated poles awaiting bundling. Orders flagged “Bundled” will queue their allocated poles here.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="awaiting-bundling">
            {bundleGroups.map((g) => (
              <div key={g.key} className="panel p-3 border-indigo-200" data-testid={`bundle-group-${g.key}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-sm">{g.order_number ? `Order ${g.order_number}` : (g.job_ref || "—")}</span>
                  <div className="text-xs bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded-sm font-bold">{g.qty} pcs</div>
                </div>
                {g.note && <div className="text-xs text-stone-600 italic mb-1">“{g.note}”</div>}
                <div className="text-xs text-stone-600 mb-2 space-y-0.5">
                  <div className="font-mono">{g.qty}× {g.colour || "—"} · {g.butt_size ?? "—"}mm · {g.length ?? "—"}m</div>
                  <div className="text-stone-500">{g.ppb ? `${g.ppb} poles / bundle` : "no bundle size set — bundles all"}</div>
                </div>
                {canCreate && (
                  <div className="flex flex-col gap-1.5">
                    <Btn variant="secondary" className="w-full" onClick={() => downloadJobSheet("bundling", g.ids)} testid={`bundle-jobsheet-${g.key}`}>
                      <FileText className="h-3.5 w-3.5" /> Download Job Sheet (PDF)
                    </Btn>
                    {g.ppb ? (() => {
                      const maxBundles = Math.ceil(g.qty / g.ppb);
                      const raw = bundleCounts[g.key];
                      const cnt = Math.min(Math.max(1, Number(raw) || 1), maxBundles);
                      const poles = Math.min(cnt * g.ppb, g.qty);
                      return (
                        <div className="flex items-stretch gap-1.5">
                          <Input
                            type="number" min="1" max={maxBundles}
                            value={raw ?? ""}
                            onChange={(e) => setBundleCounts((c) => ({ ...c, [g.key]: e.target.value }))}
                            placeholder="1"
                            className="w-20 text-center"
                            data-testid={`bundle-count-${g.key}`}
                          />
                          <Btn className="flex-1" onClick={() => createBundle(g, cnt)} testid={`create-bundle-${g.key}`}>
                            <Package className="h-3.5 w-3.5" /> Mark {cnt} of {maxBundles} Bundle(s) Done ({poles} poles)
                          </Btn>
                        </div>
                      );
                    })() : (
                      <Btn className="w-full" onClick={() => createBundle(g, 1)} testid={`create-bundle-${g.key}`}>
                        <Package className="h-3.5 w-3.5" /> Create Bundle (all {g.qty} poles)
                      </Btn>
                    )}
                    <Btn variant="secondary" className="w-full text-red-700 hover:bg-red-50" onClick={() => removeFinishing("bundling", g)} testid={`remove-bundle-job-${g.key}`}>
                      <Trash2 className="h-3.5 w-3.5" /> Remove Job
                    </Btn>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Toolbar>
        <SearchInput value={q} onChange={setQ} placeholder="Search processing records..." />
        <div className="text-xs text-stone-500 ml-auto">{filtered.length} processing events</div>
      </Toolbar>

      <DataTable
        columns={[
          { header: "Date", key: "date_processed", mono: true },
          { header: "Receipt", mono: true, render: (r) => <span className="text-xs">{r.receiving_id?.slice(0, 8)}…</span> },
          { header: "Clean Batches", align: "right", mono: true, render: (r) => r.batches?.length || 0 },
          { header: "Salvage Batches", align: "right", mono: true, render: (r) => r.damaged_batches?.length || 0 },
          { header: "Processed", mono: true, align: "right", render: (r) => fmtNumber(r.total_processed) },
          { header: "Salvaged", mono: true, align: "right", render: (r) => fmtNumber(r.total_damaged) },
          { header: "Batch Detail", render: (r) => (
            <div className="text-xs space-y-0.5">
              {(r.batches || []).map((b, i) => (
                <div key={`b${i}`} className="font-mono">
                  {b.quantity}× {b.length}m · {b.butt_size}mm · <span className="font-semibold">{b.colour}</span>
                </div>
              ))}
              {(r.damaged_batches || []).map((b, i) => (
                <div key={`d${i}`} className="font-mono text-amber-800">
                  ⚠ {b.quantity}× {b.damaged_length}m→{b.accepted_length}m · {b.butt_size}mm · <span className="font-semibold">{b.colour}</span>
                </div>
              ))}
            </div>
          )},
        ]}
        rows={filtered}
      />

      {target && (
        <ProcessModal receipt={target} colourMap={colourMap} onClose={() => { setTarget(null); load(); }} />
      )}
      {salvageTarget && (
        <SalvageModal stock={salvageTarget} colourMap={colourMap} onClose={() => { setSalvageTarget(null); load(); }} />
      )}
      {combineItems && (
        <Modal open onClose={() => setCombineItems(null)} title={`Combined Cut List — ${combineSel.length} plans`} size="md">
          <div className="space-y-3" data-testid="combine-priority-modal">
            <p className="text-sm text-stone-600">
              These are the line items required across the selected plans. Set the priority with the
              arrows — the top line prints first on the sheet so it gets cut first. Completed lines
              are left off automatically.
            </p>
            <div className="space-y-1">
              {combineItems.map((it, i) => (
                <div key={i} className="flex items-center gap-2 text-xs font-mono bg-stone-50 border border-stone-200 rounded-sm px-2 py-1.5"
                     data-testid={`combine-item-${i}`}>
                  <span className="font-bold text-teal-800 w-5">{i + 1}.</span>
                  <span className="inline-block h-3 w-3 rounded-full border border-stone-300 shrink-0" style={{ background: swatchFor(it.colour) }} />
                  <span className="font-semibold">{it.colour || "—"}</span>
                  <span className="text-stone-500">· {it.length != null ? `${it.length}m` : "—"} · ⌀{it.butt_size != null ? `${it.butt_size}mm` : "—"}</span>
                  <span className="font-semibold text-stone-700">{fmtNumber(it.qty)}×</span>
                  <span className="text-stone-400 text-[10px] truncate">({[...it.refs].join(", ")})</span>
                  <span className="ml-auto flex gap-0.5">
                    <button onClick={() => moveCombineItem(i, -1)} disabled={i === 0}
                            className="p-0.5 rounded-sm hover:bg-stone-200 disabled:opacity-30"
                            data-testid={`combine-up-${i}`}><ArrowUp className="h-3.5 w-3.5" /></button>
                    <button onClick={() => moveCombineItem(i, 1)} disabled={i === combineItems.length - 1}
                            className="p-0.5 rounded-sm hover:bg-stone-200 disabled:opacity-30"
                            data-testid={`combine-down-${i}`}><ArrowDown className="h-3.5 w-3.5" /></button>
                  </span>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Btn variant="secondary" onClick={() => setCombineItems(null)} testid="combine-cancel-btn">Cancel</Btn>
              <Btn onClick={downloadCombined} testid="combine-download-btn">
                <FileText className="h-3.5 w-3.5" /> Download Combined PDF
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {stockCutOpen && (
        <CutPlanModal initialSpecs={typeof stockCutOpen === "object" ? stockCutOpen.specs : undefined}
                      onClose={() => { setStockCutOpen(false); load(); }} onApplied={load} />
      )}

      {cutReportOpen && (
        <Modal open onClose={() => setCutReportOpen(false)} title="Cutting Report — what was cut on which day" size="lg">
          <div className="space-y-3" data-testid="cut-report-modal">
            <div className="flex items-end gap-2 flex-wrap">
              <Field label="From">
                <Input type="date" value={crFrom} onChange={(e) => setCrFrom(e.target.value)} data-testid="cut-report-from" />
              </Field>
              <Field label="To">
                <Input type="date" value={crTo} onChange={(e) => setCrTo(e.target.value)} data-testid="cut-report-to" />
              </Field>
              <Btn size="sm" variant="secondary" onClick={loadCutReport} testid="cut-report-load-btn">Load</Btn>
              <span className="ml-auto flex gap-1.5">
                <Btn size="sm" variant="secondary" onClick={() => downloadCutReport("pdf")} testid="cut-report-pdf-btn">
                  <FileText className="h-3.5 w-3.5" /> PDF
                </Btn>
                <Btn size="sm" variant="secondary" onClick={() => downloadCutReport("xlsx")} testid="cut-report-xlsx-btn">
                  <FileText className="h-3.5 w-3.5" /> Excel
                </Btn>
              </span>
            </div>
            {crLoading ? (
              <div className="text-sm text-stone-400 py-8 text-center">Loading…</div>
            ) : !cutReport || (cutReport.days || []).length === 0 ? (
              <div className="text-sm text-stone-400 py-8 text-center" data-testid="cut-report-empty">
                No cutting was recorded in this period.
              </div>
            ) : (
              <>
                <div className="text-xs text-stone-600 font-mono" data-testid="cut-report-totals">
                  Period totals: <b>{fmtNumber(cutReport.totals.cut)}</b> donor poles cut ·{" "}
                  <b>{fmtNumber(cutReport.totals.created)}</b> pieces created ·{" "}
                  <b>{fmtNumber(cutReport.totals.painted)}</b> painted
                </div>
                <div className="space-y-4 max-h-[58vh] overflow-y-auto pr-1">
                  {cutReport.days.map((day) => (
                    <div key={day.date} data-testid={`cut-report-day-${day.date}`}>
                      <div className="bg-teal-800 text-white text-xs font-bold px-2 py-1.5 rounded-sm">
                        {new Date(`${day.date}T00:00:00`).toLocaleDateString("en-GB", {
                          weekday: "long", day: "2-digit", month: "short", year: "numeric",
                        })}{" "}
                        <span className="font-normal opacity-80">
                          — {fmtNumber(day.totals.cut)} cut · {fmtNumber(day.totals.created)} pieces created · {fmtNumber(day.totals.painted)} painted
                        </span>
                      </div>
                      {day.entries.map((en, ei) => (
                        <div key={ei} className="border border-stone-200 rounded-sm mt-1 p-2 text-xs">
                          <div className="font-mono font-semibold text-teal-800">
                            {en.cut_ref} · order {en.order_number || "—"}
                            {(en.by || []).length > 0 && (
                              <span className="text-stone-400 font-normal"> · {en.by.join(", ")}</span>
                            )}
                          </div>
                          {(en.cut || []).map((c, i) => (
                            <div key={i} className="mt-1.5">
                              <div className="font-mono">
                                <b>{fmtNumber(c.qty)}×</b> donor {crSpec(c)} cut
                                {c.waste_m ? <span className="text-stone-500"> · waste {c.waste_m} m</span> : null}
                              </div>
                              <div className="pl-4 text-stone-600 font-mono">
                                {(c.outputs || []).map((o, j) => (
                                  <div key={j}>
                                    → {fmtNumber(o.qty)} × {crSpec(o)}{" "}
                                    <span className={o.role === "off-cut" ? "text-amber-700" : "text-emerald-700"}>
                                      {o.role === "off-cut" ? "(off-cut → back to stock)" : "(for the order)"}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                          {(en.painted || []).map((c, i) => (
                            <div key={`p${i}`} className="mt-1 font-mono text-fuchsia-800">
                              <b>{fmtNumber(c.qty)}×</b> {crSpec(c)} painted → available stock
                            </div>
                          ))}
                          {(en.legacy_cut > 0 || en.legacy_painted > 0) && (
                            <div className="mt-1 text-stone-500 italic">
                              {en.legacy_cut > 0 && <>{fmtNumber(en.legacy_cut)} donor pole(s) cut</>}
                              {en.legacy_cut > 0 && en.legacy_painted > 0 && " · "}
                              {en.legacy_painted > 0 && <>{fmtNumber(en.legacy_painted)} painted</>}
                              {" "}— sizes not recorded (before daily detail logging)
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
    </AppLayout>
  );
}

const crSpec = (x) =>
  `${x.length != null ? `${x.length}m` : "—"} ⌀${x.butt_size != null ? `${x.butt_size}mm` : "—"} ${(x.colour || "").toUpperCase()}`.trim();

function ProcessModal({ receipt, colourMap, onClose }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [batches, setBatches] = useState([newBatch()]);
  const [damagedBatches, setDamagedBatches] = useState([]);
  const [writeOffQty, setWriteOffQty] = useState("");
  const [writeOffReason, setWriteOffReason] = useState("");
  const [damagedHoldQty, setDamagedHoldQty] = useState("");
  const [damagedHoldReason, setDamagedHoldReason] = useState("");
  const [waste, setWaste] = useState("");
  const [wasteManual, setWasteManual] = useState(false);
  const [comments, setComments] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/processing/batch-template.xlsx", { responseType: "blob" });
      saveBlob(r, "processing-template.xlsx");
    } catch (e) {
      toast.error("Failed to download template");
    }
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/processing/parse-batches", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const rows = r.data?.rows || [];
      if (!rows.length) { toast.error("No data rows found in the file"); return; }
      const mapped = rows.map((row) => ({
        length: row.length ?? "",
        thick: row.thick ?? "",
        wide: row.wide ?? "",
        quantity: row.quantity ?? 0,
        damaged: "",
        colour: row.colour || "",
        comments: "",
      }));
      setBatches((prev) => {
        const isDefaultOnly = prev.length === 1 && !prev[0].length && !prev[0].thick && !prev[0].wide;
        return isDefaultOnly ? mapped : [...prev, ...mapped];
      });
      toast.success(`Loaded ${mapped.length} batch row(s) (${r.data.total_quantity} poles)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to parse the Excel file");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const updateBatch = (i, key, val) => {
    setBatches((bs) => bs.map((b, x) => (x === i ? { ...b, [key]: val } : b)));
  };
  const updateDamaged = (i, key, val) => {
    setDamagedBatches((bs) => bs.map((b, x) => (x === i ? { ...b, [key]: val } : b)));
  };

  const totals = {
    qty: batches.reduce((a, b) => a + (Number(b.quantity) || 0), 0),
    salvaged: damagedBatches.reduce((a, b) => a + (Number(b.quantity) || 0), 0),
    hold: batches.reduce((a, b) => a + (Number(b.damaged) || 0), 0),
  };
  const consumed = totals.qty + totals.salvaged + totals.hold + (Number(writeOffQty) || 0);
  const alreadyProcessed = Number(receipt.processed_quantity) || 0;
  const remainingBefore = (receipt.quantity || 0) - alreadyProcessed;
  const remaining = remainingBefore - consumed;

  // Auto-calculate the damaged/offcut weight from each salvage row:
  // (avg pole weight ÷ original length) × cut length × quantity.
  const apw = Number(receipt.avg_pole_weight) || 0;
  const autoWaste = damagedBatches.reduce((sum, b) => {
    const dl = Number(b.damaged_length), al = Number(b.accepted_length), qy = Number(b.quantity) || 0;
    if (!dl || dl <= 0 || qy <= 0 || b.accepted_length === "" || b.accepted_length == null) return sum;
    return sum + (apw / dl) * Math.max(0, dl - al) * qy;
  }, 0);
  const effectiveWaste = wasteManual ? Number(waste || 0) : autoWaste;

  const colourOptions = [...new Set(colourMap.map((x) => x.colour))];
  const swatchFor = (name) => colourMap.find((x) => x.colour === name)?.swatch || "#cbd5e1";

  const paintPlan = (() => {
    const acc = {};
    const add = (thick, wide, qty) => {
      if (!thick || !wide || !qty) return;
      const butt = (Number(thick) + Number(wide)) / 2;
      const c = colourForButt(butt, colourMap);
      if (!c) return;
      const key = `${c.colour}@${c.butt}`;
      acc[key] = acc[key] || { ...c, qty: 0 };
      acc[key].qty += Number(qty);
    };
    for (const b of batches) add(b.thick, b.wide, b.quantity);
    for (const b of damagedBatches) add(b.thick, b.wide, b.quantity);
    return Object.values(acc);
  })();

  const save = async () => {
    if (consumed <= 0) { toast.error("Add at least one batch with quantity"); return; }
    if (remaining < 0) { toast.error(`Batches exceed receipt qty by ${-remaining}`); return; }

    for (const b of batches) {
      if (Number(b.quantity) <= 0) continue;
      if (!b.length || !b.thick || !b.wide) {
        toast.error("Each clean batch needs Length, Thick and Wide");
        return;
      }
    }
    for (const b of damagedBatches) {
      if (Number(b.quantity) <= 0) continue;
      if (!b.damaged_length || !b.accepted_length || !b.thick || !b.wide) {
        toast.error("Each salvaged batch needs Damaged length, Accepted length, Thick and Wide");
        return;
      }
      if (Number(b.accepted_length) > Number(b.damaged_length)) {
        toast.error("Accepted length cannot exceed damaged length");
        return;
      }
    }

    const cleanPayload = batches
      .filter((b) => Number(b.quantity) > 0)
      .map((b) => ({
        length: Number(b.length),
        thick: Number(b.thick),
        wide: Number(b.wide),
        quantity: Number(b.quantity),
        colour: b.colour || null,
        comments: b.comments || null,
      }));
    const damagedPayload = damagedBatches
      .filter((b) => Number(b.quantity) > 0)
      .map((b) => ({
        damaged_length: Number(b.damaged_length),
        accepted_length: Number(b.accepted_length),
        thick: Number(b.thick),
        wide: Number(b.wide),
        quantity: Number(b.quantity),
        colour: b.colour || null,
        comments: b.comments || null,
      }));

    const damagedHoldPayload = batches
      .filter((b) => Number(b.damaged) > 0)
      .map((b) => ({
        length: Number(b.length),
        thick: Number(b.thick),
        wide: Number(b.wide),
        quantity: Number(b.damaged),
        colour: b.colour || null,
        comments: b.comments || null,
      }));

    setSaving(true);
    try {
      await api.post("/processing", {
        receiving_id: receipt.id,
        date_processed: date,
        batches: cleanPayload,
        damaged_batches: damagedPayload,
        write_off_quantity: Number(writeOffQty) || 0,
        write_off_reason: writeOffReason || null,
        damaged_hold_batches: damagedHoldPayload,
        waste_per_load: Number(effectiveWaste || 0),
        comments: comments || null,
      });
      toast.success(`Processed ${totals.qty} clean + ${totals.salvaged} salvaged${Number(writeOffQty) ? ` + ${Number(writeOffQty)} written off` : ""}${totals.hold ? ` + ${totals.hold} damaged → Awaiting Salvage` : ""}`);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Modal
      open onClose={onClose}
      title={`Process Receipt · ${receipt.delivery_note_number || receipt.id.slice(0, 8)}`}
      size="xl" testid="process-modal"
      footer={<><Btn variant="secondary" onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={saving} testid="process-save">{saving ? "Saving..." : "Save Processing"}</Btn></>}
    >
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm mb-4 pb-3 border-b border-stone-200">
        <div><div className="data-label">Receipt</div><div className="font-mono">{receipt.delivery_note_number || "—"}</div></div>
        <div><div className="data-label">Supplier / Origin</div><div className="text-xs">{receipt.supplier_name || receipt.origin || "—"}</div></div>
        <div><div className="data-label">Received</div><div className="font-mono">{fmtNumber(receipt.quantity)} pcs</div></div>
        <div><div className="data-label">Remaining to Process</div><div className="font-mono">{fmtNumber(remainingBefore)} pcs{alreadyProcessed > 0 && <span className="text-stone-400"> ({fmtNumber(alreadyProcessed)} done)</span>}</div></div>
        <div><div className="data-label">Avg Incoming Pole Wt</div><div className="font-mono">{fmtNumber(receipt.avg_pole_weight, 3)} kg</div></div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <Field label="Date Processed" required>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="proc-date" />
        </Field>
        <Field label="Total Waste / Load (kg)">
          <Input type="number" step="0.01"
                 value={wasteManual ? waste : (autoWaste ? autoWaste.toFixed(2) : "")}
                 onChange={(e) => { setWasteManual(true); setWaste(e.target.value); }}
                 data-testid="proc-waste" />
          <div className="text-[11px] text-stone-500 mt-1">
            {wasteManual ? (
              <button type="button" className="underline" onClick={() => { setWasteManual(false); setWaste(""); }}>
                Reset to auto
              </button>
            ) : "Auto-calculated from salvage offcuts (weight ÷ length × cut)"}
          </div>
        </Field>
        <div className="md:col-span-1 flex items-end">
          <div className={`text-xs font-mono w-full text-right ${remaining < 0 ? "text-red-700 font-bold" : "text-stone-600"}`}>
            {totals.qty} clean + {totals.salvaged} salvaged{totals.hold ? ` + ${totals.hold} dmg` : ""} of {remainingBefore}
            <br />
            <span className={remaining < 0 ? "text-red-700 font-bold" : "text-stone-500"}>
              {remaining >= 0 ? `${remaining} remaining` : `over by ${-remaining}`}
            </span>
          </div>
        </div>
      </div>

      {/* Clean batches */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="data-label">Clean Batches — measure → cut → paint</div>
        <div className="flex items-center gap-2">
          <Btn variant="secondary" onClick={downloadTemplate} testid="download-batch-template">
            <FileText className="h-3.5 w-3.5" /> Download Template
          </Btn>
          <input ref={fileInputRef} type="file" accept=".xlsx"
                 onChange={handleExcelUpload} className="hidden" data-testid="batch-excel-input" />
          <Btn variant="secondary" onClick={() => fileInputRef.current?.click()} disabled={uploading} testid="upload-batch-excel">
            <Upload className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Upload Excel"}
          </Btn>
        </div>
      </div>
      <div className="panel overflow-hidden mb-6">
        <table className="min-w-full text-sm">
          <thead className="bg-stone-100 border-b">
            <tr>
              <th className="px-2 py-2 text-left text-xs uppercase tracking-wider w-28">Length (m)</th>
              <th className="px-2 py-2 text-left text-xs uppercase tracking-wider w-24">Thick (mm)</th>
              <th className="px-2 py-2 text-left text-xs uppercase tracking-wider w-24">Wide (mm)</th>
              <th className="px-2 py-2 text-left text-xs uppercase tracking-wider w-24">Butt (mm)</th>
              <th className="px-2 py-2 text-left text-xs uppercase tracking-wider w-32">Paint</th>
              <th className="px-2 py-2 text-right text-xs uppercase tracking-wider w-20">Qty</th>
              <th className="px-2 py-2 text-right text-xs uppercase tracking-wider w-20 text-amber-800"
                  title="Damaged — parked in Awaiting Salvage to cut later">Dmg</th>
              <th className="px-2 py-2 text-left text-xs uppercase tracking-wider">Comments</th>
              <th className="px-2 py-2 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {batches.map((b, i) => {
              const thick = Number(b.thick), wide = Number(b.wide);
              const butt = thick && wide ? ((thick + wide) / 2).toFixed(2) : "";
              const c = butt ? colourForButt(butt, colourMap) : null;
              return (
                <tr key={i} className="border-b" data-testid={`batch-row-${i}`}>
                  <td className="px-2 py-1.5">
                    <LengthInput
                      value={b.length}
                      onChange={(v) => updateBatch(i, "length", v)}
                      presets={LENGTHS}
                      listId={`batch-len-${i}`}
                      placeholder="e.g. 3.0"
                      data-testid={`batch-len-${i}`}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input type="number" step="0.1" value={b.thick}
                           onChange={(e) => updateBatch(i, "thick", e.target.value)}
                           data-testid={`batch-thick-${i}`} />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input type="number" step="0.1" value={b.wide}
                           onChange={(e) => updateBatch(i, "wide", e.target.value)}
                           data-testid={`batch-wide-${i}`} />
                  </td>
                  <td className="px-2 py-1.5 font-mono text-xs text-stone-700">{butt || "—"}</td>
                  <td className="px-2 py-1.5">
                    <div className="flex items-center gap-1.5">
                      <span className="inline-block h-3.5 w-3.5 rounded-full border border-stone-300 shrink-0"
                            style={{ background: swatchFor(b.colour || c?.colour) }} />
                      <Select value={b.colour || c?.colour || ""} onChange={(e) => updateBatch(i, "colour", e.target.value)}
                              className="!py-1 text-xs min-w-[110px]" data-testid={`batch-colour-${i}`}>
                        {!c && <option value="">—</option>}
                        {colourOptions.map((cn) => <option key={cn} value={cn}>{cn}</option>)}
                      </Select>
                    </div>
                  </td>
                  <td className="px-2 py-1.5">
                    <Input type="number" value={b.quantity}
                           onChange={(e) => updateBatch(i, "quantity", e.target.value)}
                           className="text-right"
                           data-testid={`batch-qty-${i}`} />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input type="number" min="0" value={b.damaged}
                           onChange={(e) => updateBatch(i, "damaged", e.target.value)}
                           className="text-right border-amber-300" placeholder="0"
                           title="Damaged — salvage later"
                           data-testid={`batch-dmg-${i}`} />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input value={b.comments} onChange={(e) => updateBatch(i, "comments", e.target.value)} />
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    {batches.length > 1 && (
                      <button onClick={() => setBatches(batches.filter((_, x) => x !== i))}
                              className="text-red-600 hover:text-red-800" data-testid={`batch-remove-${i}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="px-2 py-2 bg-stone-50 border-t border-stone-100 flex items-center justify-between gap-2">
          <Btn variant="secondary" onClick={() => setBatches([...batches, newBatch()])} testid="add-batch">
            <Plus className="h-3.5 w-3.5" /> Add Batch
          </Btn>
          {totals.hold > 0 && (
            <div className="text-[11px] text-amber-800" data-testid="proc-damaged-hold-note">
              {totals.hold} pole(s) in the <span className="font-semibold">Dmg</span> column will move to
              {" "}<span className="font-semibold">Damaged — Awaiting Salvage</span> to cut later.
            </div>
          )}
        </div>
      </div>

      {/* Damaged → salvaged batches */}
      <div className="data-label mb-2 flex items-center gap-2 text-amber-800">
        <AlertTriangle className="h-3.5 w-3.5" /> Damaged → Salvaged Batches
        <span className="text-stone-500 font-normal normal-case tracking-normal">— cut the damaged tip, the remainder becomes processed stock</span>
      </div>
      <div className="panel overflow-hidden mb-4 border-amber-200">
        <table className="min-w-full text-sm">
          <thead className="bg-amber-50 border-b border-amber-200">
            <tr>
              <th className="px-2 py-2 text-left text-xs uppercase tracking-wider w-32">Damaged Length (m)</th>
              <th className="px-2 py-2 text-left text-xs uppercase tracking-wider w-32">Accepted Length (m)</th>
              <th className="px-2 py-2 text-left text-xs uppercase tracking-wider w-24">Thick (mm)</th>
              <th className="px-2 py-2 text-left text-xs uppercase tracking-wider w-24">Wide (mm)</th>
              <th className="px-2 py-2 text-left text-xs uppercase tracking-wider w-24">Butt (mm)</th>
              <th className="px-2 py-2 text-left text-xs uppercase tracking-wider w-32">Paint</th>
              <th className="px-2 py-2 text-right text-xs uppercase tracking-wider w-20">Qty</th>
              <th className="px-2 py-2 text-left text-xs uppercase tracking-wider w-24">Cut (m/pole)</th>
              <th className="px-2 py-2 text-left text-xs uppercase tracking-wider">Comments</th>
              <th className="px-2 py-2 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {damagedBatches.length === 0 && (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-xs text-stone-400">
                  No damaged-pole salvage rows. Click &quot;Add Salvage Row&quot; if a pole had to be cut shorter due to damage.
                </td>
              </tr>
            )}
            {damagedBatches.map((b, i) => {
              const thick = Number(b.thick), wide = Number(b.wide);
              const butt = thick && wide ? ((thick + wide) / 2).toFixed(2) : "";
              const c = butt ? colourForButt(butt, colourMap) : null;
              const dl = Number(b.damaged_length), al = Number(b.accepted_length);
              const cut = (dl && al && dl >= al) ? (dl - al).toFixed(2) : "";
              const cutInvalid = dl && al && al > dl;
              const qy = Number(b.quantity) || 0;
              const wkg = (apw && dl && b.accepted_length !== "" && b.accepted_length != null)
                ? (apw / dl) * Math.max(0, dl - al) * qy : 0;
              return (
                <tr key={i} className="border-b" data-testid={`dbatch-row-${i}`}>
                  <td className="px-2 py-1.5">
                    <LengthInput
                      value={b.damaged_length}
                      onChange={(v) => updateDamaged(i, "damaged_length", v)}
                      presets={LENGTHS}
                      listId={`dbatch-dlen-${i}`}
                      placeholder="e.g. 3.0"
                      data-testid={`dbatch-dlen-${i}`}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <LengthInput
                      value={b.accepted_length}
                      onChange={(v) => updateDamaged(i, "accepted_length", v)}
                      presets={LENGTHS}
                      listId={`dbatch-alen-${i}`}
                      placeholder="e.g. 2.5"
                      data-testid={`dbatch-alen-${i}`}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input type="number" step="0.1" value={b.thick}
                           onChange={(e) => updateDamaged(i, "thick", e.target.value)}
                           data-testid={`dbatch-thick-${i}`} />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input type="number" step="0.1" value={b.wide}
                           onChange={(e) => updateDamaged(i, "wide", e.target.value)}
                           data-testid={`dbatch-wide-${i}`} />
                  </td>
                  <td className="px-2 py-1.5 font-mono text-xs text-stone-700">{butt || "—"}</td>
                  <td className="px-2 py-1.5">
                    <div className="flex items-center gap-1.5">
                      <span className="inline-block h-3.5 w-3.5 rounded-full border border-stone-300 shrink-0"
                            style={{ background: swatchFor(b.colour || c?.colour) }} />
                      <Select value={b.colour || c?.colour || ""} onChange={(e) => updateDamaged(i, "colour", e.target.value)}
                              className="!py-1 text-xs min-w-[110px]" data-testid={`dbatch-colour-${i}`}>
                        {!c && <option value="">—</option>}
                        {colourOptions.map((cn) => <option key={cn} value={cn}>{cn}</option>)}
                      </Select>
                    </div>
                  </td>
                  <td className="px-2 py-1.5">
                    <Input type="number" value={b.quantity}
                           onChange={(e) => updateDamaged(i, "quantity", e.target.value)}
                           className="text-right"
                           data-testid={`dbatch-qty-${i}`} />
                  </td>
                  <td className="px-2 py-1.5 font-mono text-xs">
                    {cutInvalid ? <span className="text-red-600 font-semibold">invalid</span>
                      : cut ? <span className="text-amber-800">{cut} m{wkg ? ` · ${wkg.toFixed(1)} kg` : ""}</span> : "—"}
                  </td>
                  <td className="px-2 py-1.5">
                    <Input value={b.comments} onChange={(e) => updateDamaged(i, "comments", e.target.value)} />
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <button onClick={() => setDamagedBatches(damagedBatches.filter((_, x) => x !== i))}
                            className="text-red-600 hover:text-red-800" data-testid={`dbatch-remove-${i}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="px-2 py-2 bg-amber-50 border-t border-amber-100">
          <Btn variant="secondary" onClick={() => setDamagedBatches([...damagedBatches, newDamagedBatch()])} testid="add-damaged-batch">
            <Plus className="h-3.5 w-3.5" /> Add Salvage Row
          </Btn>
        </div>
      </div>

      {/* Write-offs: poles found not salvageable while measuring */}
      <div className="panel p-3 mb-4 border-red-200">
        <div className="data-label mb-2 flex items-center gap-2 text-red-800">
          <AlertTriangle className="h-3.5 w-3.5" /> Not Salvageable (Write-off)
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <Field label="Quantity to write off">
            <Input type="number" min="0" value={writeOffQty}
                   onChange={(e) => setWriteOffQty(e.target.value)}
                   placeholder="0" data-testid="proc-writeoff-qty" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Reason (optional)">
              <Input value={writeOffReason} onChange={(e) => setWriteOffReason(e.target.value)}
                     placeholder="e.g. Rot / split beyond use" data-testid="proc-writeoff-reason" />
            </Field>
          </div>
        </div>
        {Number(writeOffQty) > 0 && (
          <div className="text-[11px] text-red-700 mt-1">
            {Number(writeOffQty)} pole(s) will be consumed from this receipt and recorded as written off (removed from inventory).
          </div>
        )}
      </div>

      {paintPlan.length > 0 && (
        <div className="panel p-3 mb-4 bg-emerald-50/40 border-emerald-200">
          <div className="data-label mb-2 flex items-center gap-2 text-emerald-900"><Palette className="h-3.5 w-3.5" /> Paint Instructions</div>
          <div className="flex flex-wrap gap-2">
            {paintPlan.map((p) => (
              <div key={`${p.butt}-${p.colour}`} className="flex items-center gap-2 px-3 py-1.5 bg-white border border-stone-200 rounded-sm">
                <span className="inline-block h-4 w-4 rounded-full border border-stone-300" style={{ background: p.swatch }} />
                <span className="font-semibold text-sm uppercase tracking-wider">{p.colour}</span>
                <span className="text-xs text-stone-500">·</span>
                <span className="font-mono text-sm">{p.qty} poles ({p.butt} mm)</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Field label="Comments">
        <Textarea rows={2} value={comments} onChange={(e) => setComments(e.target.value)} />
      </Field>
    </Modal>
  );
}


// ============ SALVAGE MODAL (damaged stock → cut to accepted length) ============
const newSalvageRow = () => ({ quantity: "", accepted_length: "", thick: "", wide: "" });

function SalvageModal({ stock, colourMap, onClose }) {
  const [rows, setRows] = useState([newSalvageRow()]);
  const [writeOffQty, setWriteOffQty] = useState("");
  const [polesUsed, setPolesUsed] = useState("");
  const [comments, setComments] = useState("");
  const [saving, setSaving] = useState(false);

  const origLen = Number(stock.length) || 0;
  const sourceQty = Number(stock.quantity) || 1;

  const setRow = (i, patch) => setRows((rs) => rs.map((r, ri) => (ri === i ? { ...r, ...patch } : r)));
  const removeRow = (i) => setRows((rs) => rs.filter((_, ri) => ri !== i));

  const salvagedTotal = rows.reduce((a, r) => a + (Number(r.quantity) || 0), 0);
  const outLen = rows.reduce((a, r) => a + (Number(r.quantity) || 0) * (Number(r.accepted_length) || 0), 0);
  const polesUsedEff = Number(polesUsed) > 0 ? Number(polesUsed) : salvagedTotal;
  const badPolesUsed = salvagedTotal > 0 && (polesUsedEff < 1 || polesUsedEff > salvagedTotal);
  const overWood = !!origLen && outLen > polesUsedEff * origLen + 1e-6;
  const totalWaste = origLen ? Math.max(0, polesUsedEff * origLen - outLen) : 0;
  const consumed = polesUsedEff + (Number(writeOffQty) || 0);
  const remaining = sourceQty - consumed;
  const over = consumed > sourceQty;

  const rowMeta = (r) => {
    const thick = Number(r.thick) || Number(stock.thick) || 0;
    const wide = Number(r.wide) || Number(stock.wide) || 0;
    const butt = thick && wide ? ((thick + wide) / 2).toFixed(1) : (stock.butt_size ?? "");
    const c = butt ? colourForButt(butt, colourMap) : null;
    const acc = Number(r.accepted_length) || 0;
    const badLen = origLen && acc && acc > origLen;
    const waste = origLen && acc && !badLen && polesUsedEff === salvagedTotal ? (origLen - acc).toFixed(2) : "";
    return { butt, c, waste, badLen };
  };

  const save = async () => {
    const batches = rows.filter((r) => Number(r.quantity) > 0 || r.accepted_length);
    if (!batches.length && !(Number(writeOffQty) > 0)) {
      toast.error("Add at least one salvage line or a write-off quantity"); return;
    }
    for (const r of batches) {
      if (!(Number(r.quantity) > 0)) { toast.error("Each salvage line needs a quantity"); return; }
      if (!(Number(r.accepted_length) > 0)) { toast.error("Each salvage line needs an accepted length"); return; }
      if (origLen && Number(r.accepted_length) > origLen) { toast.error("Accepted length cannot exceed the original length"); return; }
    }
    if (over) { toast.error(`Poles cut + written off (${consumed}) exceeds the damaged quantity (${sourceQty})`); return; }
    if (badPolesUsed) { toast.error(`Damaged poles cut must be between 1 and the salvaged piece count (${salvagedTotal})`); return; }
    if (overWood) { toast.error(`Salvaged lengths total ${outLen.toFixed(2)}m — more wood than ${polesUsedEff} pole(s) of ${origLen}m can yield`); return; }
    setSaving(true);
    try {
      const res = await api.post("/processing/salvage-multi", {
        stock_item_id: stock.id,
        batches: batches.map((r) => ({
          quantity: Number(r.quantity),
          accepted_length: Number(r.accepted_length),
          thick: Number(r.thick) || null,
          wide: Number(r.wide) || null,
        })),
        poles_used: polesUsedEff,
        write_off_quantity: Number(writeOffQty) || 0,
        comments: comments || null,
      });
      const d = res.data || {};
      toast.success(`Salvaged ${d.salvaged || 0} piece(s) from ${d.poles_used || 0} pole(s)${d.written_off ? ` · ${d.written_off} written off` : ""}${d.remaining ? ` · ${d.remaining} still damaged` : ""}`);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Modal
      open onClose={onClose}
      title={`Salvage Damaged Poles · ${stock.bundle_number || stock.id.slice(0, 8)}`}
      size="xl" testid="salvage-modal"
      footer={<><Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} disabled={saving || over || overWood || badPolesUsed} testid="salvage-save">{saving ? "Saving..." : `Salvage ${salvagedTotal || ""} Piece(s)`}</Btn></>}
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-4 pb-3 border-b border-stone-200">
        <div><div className="data-label">Product</div><div>{stock.product_name || "—"}</div></div>
        <div><div className="data-label">Original Length</div><div className="font-mono">{stock.length ? `${stock.length} m` : "—"}</div></div>
        <div><div className="data-label">Butt / Colour</div><div className="font-mono">{stock.butt_size ?? "—"}mm · {stock.colour || "—"}</div></div>
        <div><div className="data-label">Damaged Quantity</div><div className="font-mono font-bold">{sourceQty}</div></div>
      </div>

      <div className="text-xs text-stone-500 mb-2">
        Add one line per salvaged size — different lengths and butt diameters can be captured together. Each line becomes its own Awaiting-Paint stock line.
        If a pole was damaged in the middle and cut into several shorter pieces, capture each piece and set <span className="font-semibold">Damaged poles cut</span> below to the number of poles actually used.
      </div>

      <div className="space-y-2 mb-3" data-testid="salvage-rows">
        {rows.map((r, i) => {
          const m = rowMeta(r);
          return (
            <div key={i} className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end panel p-2.5 bg-stone-50/60">
              <Field label="Qty" required>
                <Input type="number" min="1" value={r.quantity} onChange={(e) => setRow(i, { quantity: e.target.value })} data-testid={`salvage-row-qty-${i}`} />
              </Field>
              <Field label="Accepted Length (m)" required>
                <LengthInput value={r.accepted_length} onChange={(v) => setRow(i, { accepted_length: v })} presets={LENGTHS}
                             listId={`salvage-alen-${i}`} placeholder="e.g. 2.5" data-testid={`salvage-row-length-${i}`} />
              </Field>
              <Field label="Thick (mm)">
                <Input type="number" step="0.1" value={r.thick} onChange={(e) => setRow(i, { thick: e.target.value })} data-testid={`salvage-row-thick-${i}`} />
              </Field>
              <Field label="Wide (mm)">
                <Input type="number" step="0.1" value={r.wide} onChange={(e) => setRow(i, { wide: e.target.value })} data-testid={`salvage-row-wide-${i}`} />
              </Field>
              <div className="text-xs space-y-0.5">
                <div>Butt: <span className="font-mono font-semibold">{m.butt || "—"} mm</span></div>
                <div className="flex items-center gap-1">
                  {m.c ? (<><span className="inline-block h-3 w-3 rounded-full border border-stone-300" style={{ background: m.c.swatch }} />
                    <span className="uppercase tracking-wide font-semibold">{m.c.colour}</span></>) : <span className="text-stone-400">—</span>}
                </div>
                <div>Waste: {m.badLen ? <span className="text-red-600 font-semibold">invalid</span> : <span className="font-mono text-amber-800">{m.waste ? `${m.waste} m` : "—"}</span>}</div>
              </div>
              <div className="flex justify-end">
                {rows.length > 1 && (
                  <button type="button" onClick={() => removeRow(i)} className="text-stone-400 hover:text-red-600 p-1.5" data-testid={`salvage-remove-row-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Btn variant="secondary" size="sm" onClick={() => setRows((rs) => [...rs, newSalvageRow()])} testid="salvage-add-row">
        <Plus className="h-4 w-4" /> Add Size
      </Btn>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
        <Field label="Damaged poles cut (pcs)">
          <Input type="number" min="1" value={polesUsed} onChange={(e) => setPolesUsed(e.target.value)}
                 placeholder={`auto = ${salvagedTotal || 1} (1 pole per piece)`} data-testid="salvage-poles-used" />
          <div className="text-[11px] text-stone-500 mt-1">Enter fewer than the piece count when several pieces came out of the same pole (e.g. 2 pieces from 1 pole).</div>
        </Field>
        <Field label="Not salvageable — write off (pcs)">
          <Input type="number" min="0" value={writeOffQty} onChange={(e) => setWriteOffQty(e.target.value)} data-testid="salvage-writeoff-qty" />
        </Field>
        <Field label="Comments">
          <Textarea rows={2} value={comments} onChange={(e) => setComments(e.target.value)}
                    placeholder="Notes about the salvage cuts" data-testid="salvage-comments" />
        </Field>
      </div>

      <div className={`flex flex-wrap items-center gap-4 text-sm panel p-3 mt-3 ${(over || overWood || badPolesUsed) ? "bg-red-50 border-red-300" : "bg-emerald-50/40 border-emerald-200"}`} data-testid="salvage-totals">
        <div>Pieces salvaged: <span className="font-mono font-bold">{salvagedTotal}</span></div>
        <div>Poles cut: <span className={`font-mono font-bold ${badPolesUsed ? "text-red-600" : ""}`}>{salvagedTotal ? polesUsedEff : 0}</span></div>
        <div>Written off: <span className="font-mono font-bold">{Number(writeOffQty) || 0}</span></div>
        <div>Total waste: <span className={`font-mono font-bold ${overWood ? "text-red-600" : "text-amber-800"}`}>{overWood ? "TOO LONG!" : `${totalWaste.toFixed(2)} m`}</span></div>
        <div>Remaining damaged: <span className={`font-mono font-bold ${over ? "text-red-600" : ""}`}>{over ? "OVER!" : remaining}</span></div>
        <div className="text-stone-500">of {sourceQty} damaged</div>
      </div>
    </Modal>
  );
}
